import SegmentationDelegate from '../delegates/Segmentation.delegate';
import TabController from './Tab.controller';
import Inherit from '../../../core/inherit';
import {
    DEFAULT_SEGMENTATION_STATE,
    USER_CRITERIA_DEFAULT_VALUE,
    SEGMENTATION_MODE,
    CRITERIA_OPERATOR_OPTIONS
} from '../configuration.constants';
import { clone } from '../../../utils/json';
import { SCOPES } from '../../../constants/scopes.constants';

/**
 * View-model for the "User Eligibility" / Segmentation tab.
 *
 * Its render slice lives in the host component's `state.tabs.segmentation`
 * (owned via {@link TabController}); mutations route through the inherited
 * delegate setters which call `this.setState(partial)` — the component's
 * real, batched `setState`. The change-detection snapshot is a plain
 * controller field and never drives a render.
 */
class SegmentationController extends Inherit(TabController, SegmentationDelegate) {
    constructor(page) {
        super(page, 'segmentation');
        this.snapshot = null;
    }

    /**
     * Can view/manage the client access list (whitelist ALLOW rows, blocked
     * DENY rows, and bulk CSV upload) — the dedicated scope the backend
     * routes actually check, distinct from CONFIGURATIONS_MANAGE. Read fresh
     * each call rather than cached, matching `canModifyConfiguration()`'s own
     * pattern.
     */
    get canManageAccessList() {
        return this.page.hasScope(SCOPES.ACCESS_LIST_MANAGE);
    }

    /** Default render slice the host seeds `state.tabs.segmentation` with. */
    getInitialState() {
        const state = clone(DEFAULT_SEGMENTATION_STATE);
        // Initialize blockedMembers and whitelistRules separately (managed via API calls)
        state.blockedMembers = [];
        state.whitelistRules = [];
        state.selectedTab = SEGMENTATION_MODE.RESTRICTED;
        return state;
    }

    // --- Snapshot / change detection -----------------------------------

    captureSnapshot(source = this.state) {
        this.snapshot = clone({ segment_policy: source.segment_policy });
    }

    hasChanges() {
        if (!this.snapshot) return false;
        return JSON.stringify(this.state.segment_policy) !== JSON.stringify(this.snapshot.segment_policy);
    }

    // --- Cross-controller notification ---------------------------------

    /**
     * Forwards the just-computed segmentation values to AvailableBalance
     * so it can sanitize its rules in the same React batch. Values are
     * passed explicitly rather than reading them from `this.state`, which
     * still reflects the pre-update committed snapshot at this point.
     */
    notifyAvailableBalance(segmentPolicy) {
        this.page?.availableBalance?.sanitizeBalanceRulesWithSegmentation(
            segmentPolicy,
            null
        );
    }

    // --- Transforms -----------------------------------------------------

    transformToSnakeCase(segmentPolicy = {}) {
        const transformed = {
            mode: segmentPolicy.mode || SEGMENTATION_MODE.RESTRICTED,
            whitelist: Array.isArray(segmentPolicy.whitelist) ? segmentPolicy.whitelist.map(item => typeof item === 'string' ? item : item.member_id) : [],
            block_out_of_segmentation: !!segmentPolicy.blockOutOfSegmentation
        };

        if (segmentPolicy.restrictedCriteria) {
            transformed.restricted_criteria = segmentPolicy.restrictedCriteria;
        }

        return transformed;
    }

    transformToCamelCase(segmentPolicy = {}) {
        const result = {
            mode: segmentPolicy.mode || SEGMENTATION_MODE.RESTRICTED,
            whitelist: Array.isArray(segmentPolicy.whitelist) ? segmentPolicy.whitelist : [],
            blockOutOfSegmentation: !!segmentPolicy.block_out_of_segmentation
        };

        // Transform restricted_criteria and ensure all fields have values
        if (segmentPolicy.restricted_criteria) {
            const transformed = {};

            // Merge each criterion with its defaults
            for (const [key, criterion] of Object.entries(segmentPolicy.restricted_criteria)) {
                transformed[key] = {
                    ...USER_CRITERIA_DEFAULT_VALUE[key],    // Start with defaults
                    ...criterion              // Override with incoming data
                };
            }

            result.restrictedCriteria = transformed;
        }

        return result;
    }

    // --- Lifecycle (orchestrator API) ----------------------------------

    /**
     * Pure: computes and returns this tab's initial slice from the FI
     * payload without touching React state. Seeds the snapshot.
     * Called by the orchestrator, which commits all slices in one batch.
     */
    applyFromFi(fiData) {
        const next = this.getInitialState();
        if (fiData?.segment_policy) {
            const transformed = this.transformToCamelCase(fiData.segment_policy);
            next.segment_policy = transformed;
            // Sync selectedTab with the loaded mode
            next.selectedTab = transformed.mode || SEGMENTATION_MODE.RESTRICTED;
        }
        this.captureSnapshot(next);
        return next;
    }

    /**
     * Load blocked and whitelisted members from API.
     * Called after initial state is set.
     */
    async loadAccessLists() {
        // No admin:access_list:manage — the backend routes 403 on this scope
        // now, so skip the request rather than surfacing an error for data
        // the UI won't render anyway (ServiceAccessCard hides these tabs).
        if (!this.canManageAccessList) return;
        try {
            const aba = this.page.user?.fi?.aba || this.page.user?.access?.[0];
            if (aba) {
                const blockedRules = await this.page.data.fetchAccessList(aba, 'DENY');
                const allowedRules = await this.page.data.fetchAccessList(aba, 'ALLOW');

                this.setState({
                    blockedMembers: blockedRules?.length ? blockedRules : [],
                    whitelistRules: allowedRules?.length ? allowedRules : []
                });
            }
        } catch (error) {
            console.error('Error loading access lists:', error);
        }
    }

    // --- API Call Wrappers (handle API calls, then delegate to state updates) ---

    async addToWhitelistWithAPI(memberId) {
        if (!this.canManageAccessList) return;
        const aba = this.page.user?.fi?.aba || this.page.user?.access?.[0];
        if (!aba) {
            this.page.props.notificationApi.error({
                message: 'Error',
                description: 'Unable to find FI ABA'
            });
            return;
        }

        try {
            const result = await this.page.data.createAccessRule(aba, {
                member_id: memberId,
                action: 'ALLOW'
            });

            if (result) {
                this.setState((prev) => ({
                    whitelistRules: [...(prev.whitelistRules || []), result]
                }));
                this.page.props.notificationApi.success({
                    message: 'Success',
                    description: `Member ${memberId} whitelisted successfully`
                });
            }
        } catch (error) {
            const status = error?.response?.status;
            const errorMessage = error?.response?.data?.error?.message;

            if (status === 409) {
                this.page.props.notificationApi.warning({
                    message: 'Member Already Whitelisted/Blocked',
                    description: errorMessage || `Member ${memberId} is already in the whitelist`
                });
            } else {
                this.page.props.notificationApi.error({
                    message: 'Error',
                    description: errorMessage || `Failed to whitelist member ${memberId}`
                });
            }
        }
    }

    async removeFromWhitelistWithAPI(memberId, ruleId) {
        if (!this.canManageAccessList) return;
        const aba = this.page.user?.fi?.aba || this.page.user?.access?.[0];
        if (!aba) {
            this.page.props.notificationApi.error({
                message: 'Error',
                description: 'Unable to find FI ABA'
            });
            return;
        }

        try {
            await this.page.data.deleteAccessRule(aba, ruleId);
            this.removeFromWhitelist(memberId, ruleId);
            this.page.props.notificationApi.success({
                message: 'Success',
                description: `Member ${memberId} removed from whitelist`
            });
        } catch (error) {
            const errorMessage = error?.response?.data?.error?.message;
            this.page.props.notificationApi.error({
                message: 'Error',
                description: errorMessage || `Failed to remove member ${memberId}`
            });
        }
    }

    async addBlockedMemberWithAPI(memberId) {
        if (!this.canManageAccessList) return;
        const aba = this.page.user?.fi?.aba || this.page.user?.access?.[0];
        if (!aba) {
            this.page.props.notificationApi.error({
                message: 'Error',
                description: 'Unable to find FI ABA'
            });
            return;
        }

        try {
            const result = await this.page.data.createAccessRule(aba, {
                member_id: memberId,
                action: 'DENY'
            });

            if (result) {
                this.setState((prev) => ({
                    blockedMembers: [...(prev.blockedMembers || []), result]
                }));
                this.page.props.notificationApi.success({
                    message: 'Success',
                    description: `Member ${memberId} blocked successfully`
                });
            }
        } catch (error) {
            const status = error?.response?.status;
            const errorMessage = error?.response?.data?.error?.message;

            if (status === 409) {
                this.page.props.notificationApi.warning({
                    message: 'Member Already Blocked/Whitelisted',
                    description: errorMessage || `Member ${memberId} is already blocked`
                });
            } else {
                this.page.props.notificationApi.error({
                    message: 'Error',
                    description: errorMessage || `Failed to block member ${memberId}`
                });
            }
        }
    }

    async removeBlockedMemberWithAPI(memberId, ruleId) {
        if (!this.canManageAccessList) return;
        const aba = this.page.user?.fi?.aba || this.page.user?.access?.[0];
        if (!aba) {
            this.page.props.notificationApi.error({
                message: 'Error',
                description: 'Unable to find FI ABA'
            });
            return;
        }

        try {
            await this.page.data.deleteAccessRule(aba, ruleId);
            this.removeBlockedMember(memberId, ruleId);
            this.page.props.notificationApi.success({
                message: 'Success',
                description: `Member ${memberId} unblocked successfully`
            });
        } catch (error) {
            const errorMessage = error?.response?.data?.error?.message;
            this.page.props.notificationApi.error({
                message: 'Error',
                description: errorMessage || `Failed to unblock member ${memberId}`
            });
        }
    }

    /**
     * `ImportListModal` calls these instead of reaching `DataController`
     * directly, so the scope guard is a property of the write path itself,
     * not something the caller has to remember to check first — the modal is
     * a bare form, not a button `ActiveTeamCard` can hide with certainty
     * under every future call site.
     */
    async bulkUploadAccessList(formData) {
        if (!this.canManageAccessList) {
            throw new Error('Not authorized to manage the client access list');
        }
        const aba = this.page.user?.fi?.aba || this.page.user?.access?.[0];
        if (!aba) throw new Error('Unable to find FI ABA');
        return this.page.data.bulkUploadAccessList(aba, formData);
    }

    async getAccessListBulkStatus(batchId) {
        if (!this.canManageAccessList) {
            throw new Error('Not authorized to manage the client access list');
        }
        const aba = this.page.user?.fi?.aba || this.page.user?.access?.[0];
        if (!aba) throw new Error('Unable to find FI ABA');
        return this.page.data.getAccessListBulkStatus(aba, batchId);
    }

    /**
     * Returns the snake_case payload slice the orchestrator hands to the
     * API on submit-all.
     */
    getValues() {
        return this.transformToSnakeCase(this.state.segment_policy);
    }

    // --- Submit / cancel -----------------------------------------------

    async handleSubmit() {
        if (!this.page.canModifyConfiguration()) {
            return;
        }
        try {
            this.page.setLoading(true);
            const aba = this.page.user?.fi?.aba || this.page.user?.access?.[0];
            if (!aba) {
                this.page.props.notificationApi.error({
                    message: 'Error',
                    description: 'Unable to find FI ABA'
                });
                return;
            }

            const snakePolicy = this.getValues();
            const response = await this.page.data.updateFI(aba, {
                fi_info: { segment_policy: snakePolicy }
            });

            if (response) {
                this.captureSnapshot();
                this.page.props.notificationApi.success({
                    message: 'Success',
                    description: 'Segmentation configuration updated successfully'
                });
            }
        } catch (error) {
            console.error('Error updating segmentation:', error);
            this.page.props.notificationApi.error({
                message: 'Update Failed',
                description: 'Failed to update segmentation configuration'
            });
        } finally {
            this.page.setLoading(false);
        }
    }

    handleCancel() {
        if (!this.snapshot?.segment_policy) return;
        this.setSegmentPolicy(clone(this.snapshot.segment_policy));
    }

    // --- Tab Management ---

    handleServiceAccessTabChange(newTab) {
        // WHITELIST and BLOCKED are the two access-list tabs; switching into
        // either one is itself a write (it flips segment_policy.mode), so it
        // needs the same scope the row actions inside those tabs need.
        // RESTRICTED/OPEN/DISABLED stay gated only by CONFIGURATIONS_MANAGE,
        // enforced at submit via canModifyConfiguration().
        const isAccessListTab = newTab === SEGMENTATION_MODE.WHITELIST || newTab === SEGMENTATION_MODE.BLOCKED;
        if (isAccessListTab && !this.canManageAccessList) return;

        const mode = this.state.segment_policy?.mode || SEGMENTATION_MODE.RESTRICTED;

        if (newTab === 'blocked') {
            this.setState({ selectedTab: 'blocked' });
            if (mode !== SEGMENTATION_MODE.RESTRICTED) {
                this.setSegmentationMode(SEGMENTATION_MODE.RESTRICTED);
            }
        } else {
            this.setState({ selectedTab: newTab });
            this.setSegmentationMode(newTab);
        }
    }

    // --- Derived data ---------------------------------------------------

    getOptionLabel(options, value) {
        if (!value) return undefined;
        return options.find((option) => option.value === value)?.label;
    }

    getOperatorLabel(operator) {
        if (!operator) return '';
        const option = CRITERIA_OPERATOR_OPTIONS.find(opt => opt.value === operator);
        return option?.label || operator;
    }

    getSummary() {
        const segment_policy = this.snapshot?.segment_policy || this.state.segment_policy;
        const criteria = segment_policy?.restrictedCriteria || {};

        // Build summary fields based on enabled criteria
        const summary = {
            mode: segment_policy?.mode || SEGMENTATION_MODE.RESTRICTED,
            status: 'unknown',
            minTenure: 'Not Set',
            riskFactor: 'Not Set',
            overdraftThreshold: 'Not Set',
            nsfThreshold: 'Not Set',
            negativeBalanceDays: 'Not Set',
            employerTenure: 'Not Set',
        };

        // Calculate overall risk status based on enabled criteria
        const enabledCount = Object.values(criteria).filter(c => c?.enabled).length;
        if (enabledCount === 0) {
            summary.status = 'open';
        } else if (enabledCount <= 1) {
            summary.status = 'low';
        } else if (enabledCount <= 2) {
            summary.status = 'medium';
        } else {
            summary.status = 'high';
        }

        // Extract minimum tenure requirement
        if (criteria.tenure?.enabled) {
            const operator = this.getOperatorLabel(criteria.tenure.operator);
            const value = criteria.tenure.value || 0;
            summary.minTenure = `${operator} ${value} days`;
        }

        // Extract overdraft instances threshold
        if (criteria.overdraft_instances?.enabled) {
            const operator = this.getOperatorLabel(criteria.overdraft_instances.operator);
            const value = criteria.overdraft_instances.value || 0;
            const windowDays = criteria.overdraft_instances.window_days;
            summary.overdraftThreshold = windowDays ? `${operator} ${value} in the last ${windowDays} days` : `${operator} ${value}`;
        }

        // Extract NSF threshold
        if (criteria.nsf_instances?.enabled) {
            const operator = this.getOperatorLabel(criteria.nsf_instances.operator);
            const value = criteria.nsf_instances.value || 0;
            const windowDays = criteria.nsf_instances.window_days;
            summary.nsfThreshold = windowDays ? `${operator} ${value} in the last ${windowDays} days` : `${operator} ${value}`;
        }

        // Extract negative balance days
        if (criteria.negative_balance?.enabled) {
            const operator = this.getOperatorLabel(criteria.negative_balance.operator);
            const value = criteria.negative_balance.value || 0;
            const windowDays = criteria.negative_balance.window_days;
            summary.negativeBalanceDays = windowDays
                ? `${operator} ${value} consecutive days in the last ${windowDays} days`
                : `${operator} ${value} consecutive days`;
        }

        // Extract employer tenure
        if (criteria.employer_tenure?.enabled) {
            const operator = this.getOperatorLabel(criteria.employer_tenure.operator);
            const value = criteria.employer_tenure.value || 0;
            summary.employerTenure = `${operator} ${value} days`;
        }

        // Build overall risk factor summary (combines all risk-related criteria)
        const riskFactors = [];
        if (criteria.overdraft_instances?.enabled) riskFactors.push('Overdraft');
        if (criteria.nsf_instances?.enabled) riskFactors.push('NSF');
        if (criteria.negative_balance?.enabled) riskFactors.push('Negative Balance');
        if (riskFactors.length > 0) {
            summary.riskFactor = riskFactors.join(' / ');
        }

        return summary;
    }

    // --- View props -----------------------------------------------------

    getViewProps() {
        const { segment_policy, blockedMembers, whitelistRules, selectedTab } = this.state;
        const { firstTimeSetup, loading, canModify } = this.page.state;

        return {
            values: {
                ...segment_policy,
                selectedTab,
                blockedMembers: blockedMembers || [],
                whitelist: whitelistRules || [],
                savedMode: this.snapshot?.segment_policy?.mode,
                canManageAccessList: this.canManageAccessList
            },
            summary: this.getSummary(),
            loading,
            readOnly: !canModify,
            disabledSave: !canModify,
            submitText: firstTimeSetup ? 'Submit All' : 'Save',
            confirmBeforeSubmit: !firstTimeSetup,
            handlers: {
                toggleRestrictedCriterion: (key) => this.toggleRestrictedCriterion(key),
                setRestrictedCriterionOperator: (key, v) => this.setRestrictedCriterionOperator(key, v),
                setRestrictedCriterionValue: (key, v) => this.setRestrictedCriterionValue(key, v),
                setRestrictedCriterionWindow: (key, v) => this.setRestrictedCriterionWindow(key, v),
                setSegmentationMode: (v) => this.setSegmentationMode(v),
                setBlockOutOfSegmentation: (v) => this.setBlockOutOfSegmentation(v),
                handleServiceAccessTabChange: (tab) => this.handleServiceAccessTabChange(tab),
                addToWhitelist: (memberId) => this.addToWhitelistWithAPI(memberId),
                removeFromWhitelist: (memberId, ruleId) => this.removeFromWhitelistWithAPI(memberId, ruleId),
                // Client Access List (DENY overrides)
                addBlockedMember: (memberId) => this.addBlockedMemberWithAPI(memberId),
                removeBlockedMember: (memberId, ruleId) => this.removeBlockedMemberWithAPI(memberId, ruleId),
                // Bulk upload — routed through the controller (not
                // DataController directly) so admin:access_list:manage is
                // enforced on the write path itself, not left to whichever
                // component happens to call it.
                loadAccessRules: (aba) => this.loadAccessLists(aba),
                bulkUploadAccessList: (formData) => this.bulkUploadAccessList(formData),
                getAccessListBulkStatus: (batchId) => this.getAccessListBulkStatus(batchId)
            },
            onSubmit: firstTimeSetup
                ? this.page.handleContinue.bind(this.page)
                : this.handleSubmit.bind(this),
            onCancel: firstTimeSetup
                ? this.page.handleCancel.bind(this.page)
                : this.handleCancel.bind(this)
        };
    }
}

export default SegmentationController;
